/**
 * Hook orquestrador para sessão de Screen Share (Lado do Atendente)
 * Coordena signaling, WebRTC e estado da sessão
 * 
 * @author Sistema
 * @version 1.0.0
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { createLogger } from '@/lib/logger';
import { useWebRTC } from './useWebRTC';
import { useWebRTCSignaling } from './useWebRTCSignaling';
import type { 
  ScreenShareSessionState, 
  RequestPayload, 
  AcceptPayload, 
  RejectPayload, 
  AnswerPayload, 
  IceCandidatePayload,
  EndPayload 
} from './types';

// Logger para diagnóstico
const logger = createLogger('useAgentScreenShare');

/**
 * Props do hook
 */
interface UseAgentScreenShareProps {
  /** ID da conversa atual */
  conversationId: string | null;
  /** Nome do agente/atendente */
  agentName: string;
  /** URL do avatar do agente (opcional) */
  agentAvatar?: string;
  /** Se true, habilita a funcionalidade */
  enabled?: boolean;
}

/**
 * Retorno do hook
 */
interface UseAgentScreenShareReturn {
  /** Estado atual da sessão */
  sessionState: ScreenShareSessionState;
  /** Stream de vídeo remoto (quando conectado) */
  remoteStream: MediaStream | null;
  /** Estado da conexão P2P */
  connectionState: RTCPeerConnectionState;
  /** Solicita visualização de tela ao cliente */
  requestScreenShare: () => void;
  /** Encerra sessão ativa */
  endSession: () => void;
  /** Mensagem de erro, se houver */
  error: string | null;
}

/**
 * Hook para gerenciar sessão de screen share do lado do ATENDENTE
 * 
 * Fluxo:
 * 1. Atendente chama requestScreenShare()
 * 2. Aguarda accept/reject do cliente
 * 3. Se aceito, cria offer e envia
 * 4. Recebe answer e estabelece conexão
 * 5. Recebe stream remoto
 */
export function useAgentScreenShare(props: UseAgentScreenShareProps): UseAgentScreenShareReturn {
  const { conversationId, agentName, agentAvatar, enabled = true } = props;

  // Estado da sessão
  const [sessionState, setSessionState] = useState<ScreenShareSessionState>('idle');
  const [error, setError] = useState<string | null>(null);
  
  // Ref para ICE candidates pendentes (recebidos antes de setRemoteDescription)
  const pendingIceCandidatesRef = useRef<RTCIceCandidateInit[]>([]);

  // Handler para ICE candidates locais
  const handleLocalIceCandidate = useCallback((candidate: RTCIceCandidateInit) => {
    signaling.sendIceCandidate(candidate, 'agent');
  }, []);

  // Handler para mudança de estado da conexão
  const handleConnectionStateChange = useCallback((state: RTCPeerConnectionState) => {
    logger.debug('Estado da conexão mudou', { state });
    
    if (state === 'connected') {
      setSessionState('connected');
      setError(null);
    } else if (state === 'failed') {
      setSessionState('ended');
      setError('Falha na conexão P2P');
    } else if (state === 'disconnected') {
      setSessionState('reconnecting');
    }
  }, []);

  // Hook WebRTC
  const webrtc = useWebRTC({
    onIceCandidate: handleLocalIceCandidate,
    onConnectionStateChange: handleConnectionStateChange,
  });

  // Handlers de signaling
  const handleAccept = useCallback(async (data: AcceptPayload) => {
    logger.info('Cliente aceitou solicitação');
    setSessionState('connecting');
    
    try {
      // Cria offer e envia
      const offer = await webrtc.createOffer();
      signaling.sendOffer(offer);
    } catch (err) {
      logger.error('Erro ao criar offer', err);
      setError('Erro ao iniciar conexão');
      setSessionState('ended');
    }
  }, [webrtc]);

  const handleReject = useCallback((data: RejectPayload) => {
    logger.info('Cliente recusou solicitação', { reason: data.reason });
    setSessionState('ended');
    setError(data.reason || 'Solicitação recusada pelo cliente');
  }, []);

  const handleAnswer = useCallback(async (data: AnswerPayload) => {
    logger.info('Recebeu answer do cliente');
    
    try {
      await webrtc.setRemoteDescription(data.sdp);
      
      // Processa ICE candidates pendentes
      for (const candidate of pendingIceCandidatesRef.current) {
        await webrtc.addIceCandidate(candidate);
      }
      pendingIceCandidatesRef.current = [];
    } catch (err) {
      logger.error('Erro ao processar answer', err);
      setError('Erro ao estabelecer conexão');
      setSessionState('ended');
    }
  }, [webrtc]);

  const handleIce = useCallback(async (data: IceCandidatePayload) => {
    // Ignora ICE candidates próprios
    if (data.from === 'agent') return;
    
    logger.debug('Recebeu ICE candidate do cliente');
    
    // Se ainda não definiu remote description, guarda para depois
    if (webrtc.connectionState === 'new') {
      pendingIceCandidatesRef.current.push(data.candidate);
      return;
    }
    
    try {
      await webrtc.addIceCandidate(data.candidate);
    } catch (err) {
      logger.warn('Erro ao adicionar ICE candidate', err);
    }
  }, [webrtc]);

  const handleEnd = useCallback((data: EndPayload) => {
    logger.info('Sessão encerrada pelo cliente', { reason: data.reason });
    webrtc.close();
    setSessionState('ended');
    
    if (data.reason === 'error') {
      setError('Cliente encerrou devido a erro');
    }
  }, [webrtc]);

  // Hook de signaling - precisa ser definido antes de usar no handleAccept
  const signaling = useWebRTCSignaling({
    conversationId,
    enabled: enabled && !!conversationId,
    handlers: {
      onAccept: handleAccept,
      onReject: handleReject,
      onAnswer: handleAnswer,
      onIce: handleIce,
      onEnd: handleEnd,
    },
  });

  // Solicita visualização de tela
  const requestScreenShare = useCallback(() => {
    if (!conversationId || sessionState !== 'idle') {
      logger.warn('Não pode solicitar screen share', { conversationId, sessionState });
      return;
    }

    logger.info('Solicitando visualização de tela');
    setSessionState('requesting');
    setError(null);
    signaling.sendRequest(agentName, agentAvatar);
  }, [conversationId, sessionState, agentName, agentAvatar, signaling]);

  // Encerra sessão
  const endSession = useCallback(() => {
    logger.info('Encerrando sessão de screen share');
    signaling.sendEnd('agent', 'user_action');
    webrtc.close();
    setSessionState('ended');
    pendingIceCandidatesRef.current = [];
  }, [signaling, webrtc]);

  // Reset ao mudar de conversa
  useEffect(() => {
    return () => {
      if (sessionState !== 'idle' && sessionState !== 'ended') {
        webrtc.close();
      }
      setSessionState('idle');
      setError(null);
      pendingIceCandidatesRef.current = [];
    };
  }, [conversationId]);

  // Reset para idle depois de ended (permite nova sessão)
  useEffect(() => {
    if (sessionState === 'ended') {
      const timer = setTimeout(() => {
        setSessionState('idle');
        setError(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [sessionState]);

  return {
    sessionState,
    remoteStream: webrtc.remoteStream,
    connectionState: webrtc.connectionState,
    requestScreenShare,
    endSession,
    error,
  };
}
